import {makeAutoObservable, observable, toJS} from "mobx";
import type {MithraMaterializedApi} from "~/services/MithraMaterializedApi.ts";
import type {CategorizationReviewPageController} from "./CategorizationReviewPageController.ts";
import type {
    GridAggregationModel,
    GridApi,
    GridCallbackDetails, GridColDef,
    GridColumnVisibilityModel, GridEventListener,
    GridPinnedColumnFields,
    GridRowGroupingModel,
    GridRowId,
    GridRowModel,
    GridSortModel,
} from "@mui/x-data-grid-premium";
import {GRID_CHECKBOX_SELECTION_FIELD, GRID_ROOT_GROUP_ID, gridRowTreeSelector} from "@mui/x-data-grid-premium";
import {
    getColumnPinning,
    getColumnVisibilityModel,
    getSortModel,
    storeColumnPinningChange,
    storeColumnVisibilityModelChange,
    storeSortModelChange
} from "../components/review-table/review-table-muix/settings/CategorizationColumnSettings.ts";
import {GRID_REVIEW_ACTION_FIELD} from "../components/review-table/review-table-muix/cells/ReviewActionsCell.tsx";
import {ensureFirstElementsInArray, ensureLastElementsInArray, getLocalStorageJson} from "~/utils/js-utils.ts";
import type {Category, PartialValidCategory} from "~/services/classes/Category.ts";
import type {
    MatPartReviewRow,
    MatPartReviewRowResp,
    MatReviewAggregatedGroupData
} from "~/services/classes/MaterializedClasses.ts";
import {
    MatPartReviewRowState
} from "~/services/classes/MaterializedClasses.ts";
import type {
    AsyncGridRowModel,
    PartGridGroupModel,
    PartGridRowModel
} from "../components/review-table/review-table-muix/CategorizationTableDataModel.ts";
import {
    GROUP_KEY,
    GROUP_SORTABLE_KEYS,
    GROUP_VAL,
} from "../components/review-table/review-table-muix/CategorizationTableDataModel.ts";
import {
    CATEGORIZATION_TABLE_DEFAULT_PAGE_SIZE
} from "../components/review-table/review-table-muix/CategorizationTableConstants.ts";
import {BUSINESS_UNIT_FILTER_FIELD} from "./CategorizationReviewPageFilterController.ts";
import type AuthStore from "../../../authentication/AuthStore.tsx";
import {ReviewChoice} from "~/services/classes/AiClasses.tsx";
import type {MuiXGroupResponse, MuiXPartResponse} from "./CategorizationReviewApi.ts";
import type {IMuixCustomizedPagination} from "~/components/muix-data-grid/MuixCustomizedPaginationInterface.ts";
import type {BagStore} from "~/stores/BagStore.tsx";
import {flattenRelatedDataFields} from "~/services/ApiHelpers.ts";
import {ORDERING_KEY} from "~/components/muix-data-grid/controllers/BaseMuixTableController.tsx";

const GROUP_BY_SUPPLIER: {rowGroupingModel: GridRowGroupingModel, aggregationModel: GridAggregationModel} = {
    rowGroupingModel: [GROUP_KEY] satisfies Array<keyof MatPartReviewRow>,
    aggregationModel: {'p_spend': 'sum'},
}

/**
 * Controller for MuiX Data Grid
 */
class CategorizationReviewPageTableController implements IMuixCustomizedPagination {
    /**
     * Important: Do not make gridApi observable
     */
    gridApi?: GridApi

    muixGridSortModel?: GridSortModel;
    muiXGridPinnedColumns?: GridPinnedColumnFields;
    muiXGridColumnVisibilityModel?: GridColumnVisibilityModel;
    muiXGridCOlumnOrder?: GridColDef['field'][];

    public invalidSortKeyWarnings: string = '';
    // Row state info
    paginationDisabledRowIds: Set<GridRowId> = new Set<GridRowId>();
    paginationHiddenRowIds: Set<GridRowId> = new Set<GridRowId>();
    // loadingRowIds: Set<GridRowId> = new Set<GridRowId>();
    // outOfSyncRowIds: Set<GridRowId> = new Set<GridRowId>();
    // disableInteractionRowIds: Set<GridRowId> = new Set<GridRowId>();
    // In categorization table, there is a mix of string & number as id. This is causing issues.
    //  I'll force string conversion to get around this issue. Might need furthur investigation
    pageMap: Map<string, number> = new Map();
    perPageMap: Map<string, number> = new Map();
    // Base pagination
    _baseCount: number = 0;
    _basePage: number = 0;
    _basePerPage: number = CATEGORIZATION_TABLE_DEFAULT_PAGE_SIZE;
    /**
     * Map from child -> parent (if parent is not GRID_ROOT_GROUP_ID)
     */
    readonly _treeParents: Map<string, string> = new Map();

    constructor(
        public readonly controller: CategorizationReviewPageController,
        public readonly api: MithraMaterializedApi,
        private auth: AuthStore,
        private bagStore: BagStore,
    ) {
        makeAutoObservable(this, {
            gridApi: false,
            muiXGridPinnedColumns: observable.shallow,
            _treeParents: false,
        })
    }

    init() {
        this.controller.reviewPageSelectionController.init();
        this._treeParents.clear();
    }

    get sortModel(): GridSortModel {
        return this.muixGridSortModel ?? getSortModel();
    }

    onSortModelChange(sortModel, _details): void {
        this.muixGridSortModel = sortModel;
        storeSortModelChange(sortModel);
        this.setInvalidSortKeyWarnings();
        this._basePage = 0;
    }

    setInvalidSortKeyWarnings(group_by_without_group_key = false) {
        // When in grouped mode and no group is expanded, sorting on certain columns would have no effect
        if (group_by_without_group_key)
            this.invalidSortKeyWarnings = (this.sortModel.filter(
                item => GROUP_SORTABLE_KEYS.indexOf(item.field) < 0
            ).map(
                item => this.gridApi?.getColumn(item.field)?.headerName ?? ''
            ).filter(item => item).join(', ') ?? '');
        else
            this.invalidSortKeyWarnings = '';
    };

    get rowGroupingModel(): GridRowGroupingModel {
        return this.controller.isGroupBy ? GROUP_BY_SUPPLIER.rowGroupingModel : []
    }

    get aggregationModel(): GridAggregationModel {
        return this.controller.isGroupBy ? GROUP_BY_SUPPLIER.aggregationModel : {}
    }

    get pinnedColumns(): GridPinnedColumnFields {
        const pinnedColumns = this.muiXGridPinnedColumns ?? getColumnPinning();

        // TODO: As improvement we can also disable the pinning option in the menu on these columns
        pinnedColumns.left = ensureFirstElementsInArray(pinnedColumns.left, [
            // This is the only way I was able to make the SELECTION appear before the GROUPING in grouped mode...
            GRID_CHECKBOX_SELECTION_FIELD,
            // GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
        ]);

        // Maybe not needed...
        pinnedColumns.right = ensureLastElementsInArray(pinnedColumns.right, [
            GRID_REVIEW_ACTION_FIELD,
        ]);

        // Note: cached functions must not change observables
        return pinnedColumns;
    }

    onPinnedColumnsChange(pinnedColumns: GridPinnedColumnFields, details: GridCallbackDetails): void {
        console.debug('onPinnedColumnsChange', details?.reason, pinnedColumns);
        this.muiXGridPinnedColumns = pinnedColumns;
        storeColumnPinningChange(pinnedColumns);
    }

    get visibilityModel(): GridColumnVisibilityModel {
        const model = this.muiXGridColumnVisibilityModel ?? getColumnVisibilityModel();
        if (this.controller.isGroupBy) {
            // Moj:
            // Note that MuiX example uses hiding column when it wants to hide grouped column (hides grouping by default)
            //  I spent ~2 Days thinking that it shouldn't even consider grouping column when it renders the table and user
            //      should not be able to make that column visible. It turned out that it does just hide the grouping column like a
            //      normal hidden column (which I didn't expect), but it works like that.
            //      See the following and also stackblitz example
            //  https://mui.com/x/react-data-grid/row-grouping/#hide-grouped-columns

            // Ensure grouping column are hidden in grouped mode
            model[GROUP_KEY] = false;
            model[GROUP_VAL] = false;
        }
        model[BUSINESS_UNIT_FILTER_FIELD] = false; // Never show the hidden business unit column
        return model;
    }

    onVisibilityModelChange(model: GridColumnVisibilityModel, details: GridCallbackDetails): void {
        console.debug('onVisibilityModelChange', details?.reason, model);
        let newModel: GridColumnVisibilityModel;
        if (this.controller.isGroupBy) {
            // In grouped mode we always hide the grouping columns, so we do not store that change
            // Store the model, but do not modify (GROUP_KEY, GROUP_VAL)
            newModel = {...model};
            delete newModel[GROUP_KEY];
            delete newModel[GROUP_VAL];
            const storedModel = getColumnVisibilityModel();
            if (GROUP_KEY in storedModel) newModel[GROUP_KEY] = storedModel[GROUP_VAL];
            if (GROUP_VAL in storedModel) newModel[GROUP_VAL] = storedModel[GROUP_VAL];
        } else {
            newModel = model;
        }
        // Set default_visibility
        this.bagStore.categorizationColumns.forEach(col => {
            if (!col.default_visibility && newModel[col.column_name] === undefined)
                delete newModel[col.column_name];
        })
        this.muiXGridColumnVisibilityModel = newModel;
        storeColumnVisibilityModelChange(newModel);
    }

    resetStoredColumnOrder() {
        this.muiXGridCOlumnOrder = undefined;
        localStorage.removeItem(ORDERING_KEY);
    }

    get columnOrder(): GridColDef['field'][] {
        return this.muiXGridCOlumnOrder ?? getLocalStorageJson(ORDERING_KEY) ?? [];
    }

    storeColumnOrderChange: GridEventListener<'columnOrderChange'> = (_params, _event, _details) => {
        if (!this.gridApi) return;
        const order: GridColDef['field'][] = this.gridApi.getAllColumns().map(c => c.field);
        this.muiXGridCOlumnOrder = order;
        localStorage.setItem(ORDERING_KEY, JSON.stringify(order));
    }

    applyColumnOrdering<T extends GridColDef>(columns: T[]): T[] {
        const order: string[] = this.columnOrder;
        if (order.length === 0) {
            if (this.bagStore.configStore.databagConfig?.columns) {
                return columns.sort((a, b) => {
                    if (this.bagStore.configStore.getDefaultOrder(b.field) === null) return -1;
                    if (this.bagStore.configStore.getDefaultOrder(a.field) === null) return 1;
                    return this.bagStore.configStore.getDefaultOrder(a.field)! - this.bagStore.configStore.getDefaultOrder(b.field)!;
                });
            }
            return columns;
        }
        columns.sort((a, b) => {
            const aIndex = order.indexOf(a.field);
            const bIndex = order.indexOf(b.field);
            if (aIndex === -1 && bIndex === -1) return 0; // both not in order
            if (aIndex === -1) return 1; // a not in order, b is
            if (bIndex === -1) return -1; // b not in order, a is
            return aIndex - bIndex; // both in order, sort by index
        })
        return columns;
    }

    transformGetRows(
        data: MuiXGroupResponse | MuiXPartResponse,
        group_by: string | null, group_key: string | null, relatedData: Record<string, string[]>
    ): MuiXGroupResponse | MuiXPartResponse {
        if (!this.gridApi) throw Error('gridApi not set, can not proceed!');
        let paginationRow: PartGridRowModel | undefined;
        const gridApi = this.gridApi;
        if (!group_key)
            this._baseCount = data.rowCount;
        if (group_by && !group_key) {
            data.rows = (data.rows as MatReviewAggregatedGroupData[]).map(this.toGroupModel);
        } else {
            data.rows = (data.rows as MatPartReviewRowResp[]).map(d => this.toPartModel(d, relatedData));
            if (group_by && group_key) {
                // Add subgroup pagination row
                paginationRow = this.createSubGroupPagination(group_key);
                data.rows.push(paginationRow);
                this.setDisabledHidden();
            }
            data.rows.forEach(row => this.paginationHiddenRowIds.delete(this.rowId(row)));
        }
        if (group_by) {
            // FIXME: Is it safe to assume everything will be deselected at all times?
            this.controller.reviewPageSelectionController.onLoadGroups(data.rowCount);
        } else {
            // FIXME: Is it safe to assume everything will be deselected at all times?
            this.controller.reviewPageSelectionController.onLoadSingles(data.rowCount);
        }
        this.controller.onNoError();
        this.setInvalidSortKeyWarnings(Boolean(group_by ?? false) && !(group_key ?? false));
        if (paginationRow && gridApi.getAllRowIds().some(rowId => rowId === paginationRow.id))
            gridApi.updateRows([{id: paginationRow.id, _action: 'delete'}, paginationRow]);
        return data;
    }

    rowId(row: GridRowModel): GridRowId {
        return row.id || String(row.part);
    }

    /**
     * The user has selected a category or wants to accept the suggestion for a single row
     * @param id Row ID
     * @param category New category, or null to simply accept the suggestion
     */
    onReviewCategory(id: string, category: Category | null) {
        const gridApi = this.gridApi!;
        if (!gridApi) throw new Error('gridApi not initialized')
        const reviewApi = this.controller.reviewApi;
        const bagId = this.controller.bagId;
        const revisionId = this.controller.revisionId;

        const girdRow = gridApi.getRow(id);
        if (!girdRow) throw new Error(`Cannot find row to update category for id: ${id}`)
        // Note that group could be empty string
        if (girdRow.group !== undefined) {
            // It's not supported to change category for group rows, use the selection instead
            throw new Error('Not implemented yet: onReviewCategory() for group rows');
        }

        const updateRow = girdRow as PartGridRowModel;
        const updateRowId = this.rowId(updateRow);
        const updateRowPartId = updateRow.part;
        const newCategory = toJS(category ?? updateRow.cat); // Ensure no observables are send to MuiX

        if (!this.controller.isGroupBy) {
            // Review of single row

            // Immediately show reaction to user
            const intermediateRow: PartGridRowModel = {
                ...updateRow,
                cat: newCategory,
                isLoading: true,
            }
            // noinspection TypeScriptValidateTypes
            gridApi.updateRows([intermediateRow]);

            // Call the API
            reviewApi.updateRow(bagId, revisionId, updateRowPartId, newCategory)
                .then(() => {
                    gridApi.dataSource.cache.clear();
                    this.controller.onNoError();
                    // get row from BE
                    reviewApi.getByPartIds(bagId, revisionId, [updateRowPartId])
                        .then(data => {
                            if (data.length === 0) {
                                // The row is somehow not present on the server anymore
                                console.warn('Row cannot be found anymore')
                                this.setRows(String(updateRowId), {isLoading: false, isOutOfSync: true}, true);
                            } else if (data.length === 1) {
                                // Update the row (by directly calling the API)
                                // noinspection TypeScriptValidateTypes
                                gridApi.updateRows(data);
                            } else {
                                throw new Error(`Unexpected number of rows received: ${data.length}`);
                            }
                        });
                    return;
                })
                .catch(reason => {
                    gridApi.dataSource.cache.clear();
                    this.controller.onError(reason);
                    this.setRows(String(updateRowId), {isLoading: false, isOutOfSync: true});
                    return;
                });
        } else {
            // We are updating a child row inside a group

            // From docs: https://mui.com/x/react-data-grid/row-updates/#the-rows-prop
            // Updating the rows prop causes the Data Grid to recompute the row tree, resulting in losing the current
            // tree information like the expanded rows state. Unless the re-computation is explicitly required,
            // the API method updateRows() should be used.

            // Set intermediate state
            this.updatePrivateChildren(); // Save tree structure
            // MuiX Bug CAT-3005: Setting the intermediate state destroys the tree structure
            const rowIds = new Set([String(updateRowId)]);
            this.setGroupedRows(rowIds, newCategory, {isLoading: true});

            // Call the API
            reviewApi.updateRow(bagId, revisionId, updateRowPartId, newCategory)
                .then(() => {
                    gridApi.dataSource.cache.clear();
                    this.controller.onNoError();
                    // get row from BE
                    reviewApi.getByPartIds(bagId, revisionId, [updateRowPartId])
                        .then(data => {
                            let rowData: PartGridRowModel | null = null;
                            if (data.length === 0) {
                                // The row is somehow not present on the server anymore
                                console.warn('Row cannot be found anymore')
                                rowData = {
                                    ...gridApi.getRow(updateRowId),
                                    isLoading: false,
                                    isOutOfSync: true,
                                    // Optimistic update
                                    state: MatPartReviewRowState.reviewed,
                                    ...(newCategory ? {reasoning: 'User review'} : {})
                                } as PartGridRowModel
                            } else if (data.length === 1) {
                                rowData = data[0];
                            } else {
                                throw new Error(`Unexpected number of rows received: ${data.length}`);
                            }
                            this.setGroupedRows(rowIds, newCategory, rowData)
                        });
                    return;
                })
                .catch(reason => {
                    gridApi.dataSource.cache.clear();
                    this.controller.onError(reason);
                    return;
                });
        }
    }

    /**
     * The user has selected a category or wants to accept all selected rows
     * @param category
     */
    onReviewSelectionCategory(category: PartialValidCategory | null) {
        const gridApi = this.gridApi!;
        if (!gridApi) throw new Error('gridApi not initialized')
        const reviewApi = this.controller.reviewApi;
        const selectionController = this.controller.reviewPageSelectionController;
        const filterModel = this.controller.reviewPageFilterController.apiGridFilterModel;
        const bagId = this.controller.bagId;
        const revisionId = this.controller.revisionId;

        const catOrNull = (toJS(category) ?? null) as Category | null;  // Ensure no observables are send to MuiX

        if (!selectionController.hasSelection) {
            console.warn('Cannot update, nothing is selected')
            return;
        }

        // Store the selection
        const selectedRowsInView = new Set(selectionController.getSelectedRowsInView().map(row => String(this.rowId(row))));
        const allSelectedRowsData = Array(...selectionController.selectedRows.values()).map(row => ({
            part_id: row.part,
            cat: category ?? row.cat
        } satisfies {part_id: number, cat: Category}))
        const selectedGroupsInView = new Set(selectionController.getSelectedGroupsInView().map(row => String(this.rowId(row))));
        const allSelected = selectionController.isAllSelected;
        const rowIds = selectionController.selection.singleSelectedRows.map(row => Number(this.rowId(row)));
        const suppIds = selectionController.selection.fullSelectedGroups.flatMap(row => Number(this.rowId(row)));

        // Clear the selection
        selectionController.clearSelection();

        if (!this.controller.isGroupBy) {
            if (allSelected) {
                this.setRows(null, {isLoading: true, isOutOfSync: true});
                // Call the API
                reviewApi.updateAll(bagId, revisionId, filterModel, category ?? null)
                    .then(() => {
                        this.controller.onNoError();
                        // Denote all the rows as out of sync, as we do not want to remove them from view when the
                        // filter is active
                        this.setRows(null, {isLoading: false, isOutOfSync: true}, true, catOrNull);
                    })
                    .catch(reason => {
                        this.controller.onError(reason as Error)
                        this.setRows(null, {isLoading: false, isOutOfSync: true});
                    })
                    .finally(() => {
                        gridApi.dataSource.cache.clear();
                    });
                return;
            } else {
                // Some specific rows are selected (in and out of view)
                this.setRows(new Set(allSelectedRowsData.map(i => String(i.part_id))),
                    {isLoading: true, isOutOfSync: true});
                reviewApi.updateManyRowsPipe(bagId, revisionId, allSelectedRowsData)
                    .then(() => {
                        this.controller.onNoError();
                        this.setRows(selectedRowsInView, {isLoading: false, isOutOfSync: true}, true, catOrNull);
                    })
                    .catch(reason => {
                        this.controller.onError(reason as Error);
                        this.setRows(null, {isLoading: false, isOutOfSync: true});
                    })
                    .finally(() => {
                        gridApi.dataSource.cache.clear();
                    });
                return;
            }
        } else {
            this.updatePrivateChildren(); // Save tree structure
            if (allSelected) {
                // Set all rows as loading
                this.setAllGroupedRows(
                    null,
                    {isLoading: true, isOutOfSync: true, hotfixV8DisableInteractions: true},
                    {isLoading: true, isOutOfSync: true},
                );
                // Call the API
                reviewApi.updateAll(bagId, revisionId, filterModel, category ?? null)
                    .then(() => {
                        this.controller.onNoError();
                        this.setAllGroupedRows(
                            catOrNull,
                            {isLoading: false, isOutOfSync: true, hotfixV8DisableInteractions: true},
                            {isLoading: false, isOutOfSync: true},
                        );
                    })
                    .catch(reason => {
                        this.controller.onError(reason as Error)
                        this.setAllGroupedRows(
                            null,
                            {isLoading: false, isOutOfSync: true, hotfixV8DisableInteractions: true},
                            {isLoading: false, isOutOfSync: true},
                        );
                    })
                    .finally(() => {
                        gridApi.dataSource.cache.clear();
                    });
                return;
            } else {
                // Set selected rows as loading
                this.setGroupedRows(
                    selectedRowsInView, null, {isLoading: true, isOutOfSync: true},
                    selectedGroupsInView, {isLoading: true, isOutOfSync: true}
                );
                // Call the API
                reviewApi.updateByIds(bagId, revisionId, filterModel, rowIds, suppIds, catOrNull)
                    .then(() => {
                        this.controller.onNoError();
                        this.setGroupedRows(
                            selectedRowsInView, catOrNull, {isLoading: false, isOutOfSync: true},
                            selectedGroupsInView, {isLoading: false, isOutOfSync: true},
                            true
                        );
                    })
                    .catch(reason => {
                        this.controller.onError(reason as Error);
                        this.setGroupedRows(
                            selectedRowsInView, null, {isLoading: false, isOutOfSync: true},
                            selectedGroupsInView, {isLoading: false, isOutOfSync: true},
                        );
                    })
                    .finally(() => {
                        gridApi.dataSource.cache.clear();
                    });
                return;
            }
        }
    }

    private updatePrivateChildren() {
        const api = this.gridApi!;
        const tree = gridRowTreeSelector({current: api});
        let changed = false;
        api.getAllRowIds().forEach((rowId) => {
            const node = tree[rowId];
            if (node && node.parent && node.parent !== GRID_ROOT_GROUP_ID && !this._treeParents.has(rowId as string)) {
                this._treeParents.set(rowId as string, node.parent as string);
                changed = true;
            }
        })
        return changed;
    }

    private setAllGroupedRows(category: Category | null, groupData: Partial<PartGridGroupModel>, partData: Partial<PartGridRowModel>) {
        const api = this.gridApi!;
        api.setRows(api.getAllRowIds().map<GridRowModel>(_gridRowId => {
            const id = _gridRowId as string;
            const row = api.getRow(id);
            const parentId = this._treeParents.get(id);
            if (parentId) {
                // It's partData
                return {
                    ...row,
                    cat: (category ?? row.cat) as Category,
                    ...partData,
                }
            } else {
                // It's groupData
                return {
                    ...row,
                    ...groupData,
                }
            }
        }))
    }

    /**
     * Overwrite data of rows in the table
     * @param rowIds Set of row IDs to update, or null for all rows
     * @param data Data to set
     * @param optimisticUpdate whether FE should consider state & reasoning as expected updated values
     * @param category whether muix table should update category
     * @private
     */
    private setRows(rowIds: Set<string> | null | string, data: Partial<AsyncGridRowModel>, optimisticUpdate = false,
                    category: PartialValidCategory | null | undefined = undefined) {
        if (typeof rowIds === 'string') {
            rowIds = new Set<string>([rowIds]);
        }
        const api = this.gridApi!;
        if (rowIds === null) {
            api.setRows(api.getAllRowIds().map(_gridRowId => {
                const row = api.getRow(_gridRowId);
                return {
                    ...row,
                    ...data,
                    ...(optimisticUpdate ? {
                        state: MatPartReviewRowState.reviewed,
                        review_user_id: this.auth.userId,
                        review_choice: ReviewChoice.ACCEPT,
                    } : {}),
                    ...(optimisticUpdate && (category !== undefined) ? {
                        cat: (category ?? row.cat) as Category,
                    } : {})
                };
            }))
        } else {
            api.setRows(api.getAllRowIds().map(_gridRowId => {
                const row = api.getRow(_gridRowId);
                if ((rowIds as Set<string>).has(_gridRowId as string)) {
                    return {
                        ...row,
                        ...data,
                        ...(optimisticUpdate ? {
                            state: MatPartReviewRowState.reviewed,
                            review_user_id: this.auth.userId,
                            review_choice: ReviewChoice.ACCEPT,
                            reasoning: 'User review'
                        } : {}),
                        ...(optimisticUpdate && (category !== undefined) ? {
                            cat: (category ?? row.cat) as Category,
                        } : {})
                    };
                } else return row;
            }))
        }
    }

    /**
     * Set multiple rows and groups
     *
     * Updates all rows in `rowIds` with `rowData` and optionally set the `category`
     * Updates all groups in `groupIds` with `groupData`
     * For the rows that are children of the updated groups, also update with `rowData` and `category`
     * All other groups are closed (As a workaround for the bug in MuiX data grid)
     * @param rowIds
     * @param category
     * @param rowData
     * @param groupIds
     * @param groupData
     * @param optimisticUpdate whether FE should consider state & reasoning as expected updated values
     * @private
     */
    private setGroupedRows(
        rowIds: Set<string>, category: Category | null, rowData: Partial<PartGridRowModel> | null,
        groupIds?: Set<string>, groupData?: Partial<PartGridGroupModel> | null,
        optimisticUpdate = false
    ) {
        console.debug('setGroupedRows', Array.from(rowIds), category, rowData, Array.from(groupIds ?? []), groupData);
        const api = this.gridApi!;
        const rowParents = new Set(Array.from(rowIds).map(id => this._treeParents.get(id)).filter(id => !!id))
        const treeChildren = Array.from(this._treeParents.entries())
            .reduce((acc, [childId, parentId]) => {
                if (!acc.has(parentId)) {
                    acc.set(parentId, new Set<string>());
                }
                acc.get(parentId)!.add(childId);
                return acc;
            }, new Map<string, Set<string>>());

        api.setRows(api.getAllRowIds().map(_gridRowId => {
            const id = _gridRowId as string;
            const row = api.getRow(_gridRowId);

            if (rowIds.has(id)) {
                // Row in scope
                return {
                    ...row, cat: category ?? row.cat,
                    ...rowData,
                    ...(optimisticUpdate ? {
                        state: MatPartReviewRowState.reviewed,
                        review_user_id: this.auth.userId,
                        review_choice: ReviewChoice.ACCEPT,
                        ...(category ? {reasoning: 'User review'} : {}),
                    } : {})
                };
            }
            if (groupIds && groupIds.has(id)) {
                // Group in scope
                if (treeChildren.has(id)) {
                    // Group with children
                    return {...row, ...groupData, hotfixV8DisableInteractions: true} as AsyncGridRowModel;
                } else {
                    // Closed group
                    return {...row, ...groupData} as AsyncGridRowModel;
                }
            }
            if (rowParents.has(id)) {
                // Group of a row in scope
                return {...row, ...groupData, hotfixV8DisableInteractions: true} as AsyncGridRowModel;
            }
            const parentId = this._treeParents.get(id);
            if (!parentId) {
                // Grouped row that is not in scope
                if (treeChildren.has(id)) {
                    // Group with children

                    // Option A: hotfixV8DisableInteractions
                    // return {...row, hotfixV8DisableInteractions: true} as AsyncGridRowModel;
                    // Option B: Close the group (workaround for MuiX bug)
                    return row;
                } else {
                    // Group without children
                    return row;
                }
            }
            if (groupIds && groupIds.has(parentId)) {
                // Child of updated group
                return {
                    ...row, cat: category ?? row.cat,
                    ...rowData,
                    ...(optimisticUpdate ? {
                        state: MatPartReviewRowState.reviewed,
                        review_user_id: this.auth.userId,
                        review_choice: ReviewChoice.ACCEPT,
                    } : {})
                } as AsyncGridRowModel;
            } else if (rowParents.has(parentId)) {
                // Sibling of updated row
                return row;
            } else {
                // Child of group that is not in scope

                // Option A: hotfixV8DisableInteractions
                // return row
                // Option B: Close the group (workaround for MuiX bug)
                return null;
            }
        }).filter(row => !!row))
    }

    setDisabledHidden() {
        if (!this.gridApi) return;
        const gridApi = this.gridApi;
        gridApi.getAllRowIds().forEach(rowId => {
            const row = gridApi.getRow(rowId);
            if (row &&
                row['group'] === undefined &&
                this.paginationDisabledRowIds.has(rowId)) {
                if (!String(rowId).startsWith('group-pagination-'))
                    this.paginationHiddenRowIds.add(rowId);
                this.paginationDisabledRowIds.delete(rowId);
            }
        });
    }

    updateGroupPage(id: GridRowId, value: number) {
        if (!this.gridApi) return;
        const gridApi = this.gridApi;
        // Note that skipCache param in fetchRows func will not work here (IDK why), we have to clear cache explicitly
        gridApi.dataSource.cache.clear();
        // Removing rows that are out of current subgroup page, would cause error regarding selected row not existing
        // Therefore here we just hide row (out of DOM, but in grid rows
        // Disadvantage of this approach is possible high memory usage
        gridApi.getAllRowIds().forEach(rowId => {
            const row = gridApi.getRow(rowId);
            // Untyped comparison is important here, due to mix type of rowId (string | number)
            if (row && rowId == id) {
                // this.loadingRowIds.add(rowId);
                this.pageMap.set(String(rowId), value);
            }
            if (row && row['group'] === undefined && row.supplier_id == id)
                this.paginationDisabledRowIds.add(rowId);
        });
        // Use setRows to trigger a UI thread update of datagrid
        this.setRows(String(id), {isLoading: true});
        // Note that promise of fetchRows does not wait for request to finish, therefore setting disabled hidden,
        //  has no effect here
        gridApi.dataSource.fetchRows(id, {keepChildrenExpanded: true});
    }

    getGroupPage(id: GridRowId) {
        if (!this.gridApi) return 0;
        return this.pageMap.get(String(id)) ?? 0;
    }

    updateGroupPerPage(id: GridRowId, value: number) {
        if (!this.gridApi) return;
        const gridApi = this.gridApi;
        gridApi.dataSource.cache.clear();
        gridApi.getAllRowIds().forEach(rowId => {
            const row = gridApi.getRow(rowId);
            if (row && rowId == id) {
                // this.loadingRowIds.add(rowId);
                if (value !== this.perPageMap.get(String(rowId)))
                    this.pageMap.set(String(rowId), 0);
                this.perPageMap.set(String(rowId), value);
            }
            if (row && row['group'] === undefined && row.supplier_id == id)
                this.paginationDisabledRowIds.add(rowId);
        });
        this.setRows(String(id), {isLoading: true});
        gridApi.dataSource.fetchRows(id, {keepChildrenExpanded: true});
    }

    resetGroupPerPage(id: GridRowId) {
        this.pageMap.set(String(id), 0);
    }

    getGroupPerPage(id: GridRowId) {
        if (!this.gridApi) return 0;
        return this.perPageMap.get(String(id)) ?? CATEGORIZATION_TABLE_DEFAULT_PAGE_SIZE;
    }

    getGroupPageCount(id: GridRowId) {
        if (!this.gridApi) return 0;
        return this.gridApi.getRow(id)?.count ?? 0;
    }

    getGroupDisabled(id: GridRowId) {
        if (!this.gridApi) return true;
        return this.paginationDisabledRowIds.has('group-pagination-' + String(id));
    }

    refreshGroupedRow(id: GridRowId) {
        if (!this.gridApi)
            throw new Error(`Could not find gridApi`);
        const gridApi = this.gridApi;
        gridApi.updateRows([{...gridApi.getRow(id), isLoading: true}]);
        // During this update, selection could behave strangely.
        this.controller.reviewPageSelectionController.clearSelection();
        // Get single group row from server
        const bagId = this.controller.bagId;
        const revisionId = this.controller.revisionId;
        this.controller.reviewApi.getBySupplierId(
            bagId, revisionId, this.rowGroupingModel, this.aggregationModel, id
        ).then(resp => {
            console.debug('refreshGroupedRow', resp);
            gridApi.updateRows([{
                ...gridApi.getRow(id), ...resp.data[0],
                isLoading: false,
                isOutOfSync: false,
                hotfixV8DisableInteractions: false
            }]);
            gridApi.dataSource.fetchRows(id, {skipCache: true, keepChildrenExpanded: true});
        });
    }

    //region Base pagination
    get muixCustomizedPaginationDisabled() {
        return false;
    }

    get muixCustomizedPaginationCount() {
        return this._baseCount;
    }

    get muixCustomizedPaginationPage() {
        return this._basePage;
    }

    get muixCustomizedPaginationRowsPerPage() {
        return this._basePerPage;
    }

    muixCustomizedPaginationOnPageChange(page: number) {
        console.debug('muixCustomizedPaginationOnPageChange', page);
        this._basePage = page;
        if (!this.gridApi) return;
        this.gridApi.dataSource.fetchRows(undefined, {
            skipCache: true,
            paginationModel: {
                page: this._basePage,
                pageSize: this._basePerPage
            }
        });
    }

    muixCustomizedPaginationOnRowsPerPageChange(rowsPerPage: number) {
        console.debug('muixCustomizedPaginationOnRowsPerPageChange', rowsPerPage);
        this._basePage = 0;
        this._basePerPage = rowsPerPage;
        if (!this.gridApi) return;
        this.gridApi.dataSource.fetchRows(undefined, {
            skipCache: true,
            paginationModel: {
                page: this._basePage,
                pageSize: this._basePerPage
            }
        });
    }

    //endregion

    toPartModel(d: MatPartReviewRowResp, relatedData: Record<string, string[]>): PartGridRowModel {
        flattenRelatedDataFields(d, relatedData);
        return {
            ...d,
            isLoading: false,
        } as PartGridRowModel;
    }

    createSubGroupPagination(group_key: string | number): PartGridRowModel {
        return {
            id: 'group-pagination-' + String(group_key),
            supplier_id: Number(group_key),
            subGroupPagination: true,
        } as PartGridRowModel;
    }

    toGroupModel(d: MatReviewAggregatedGroupData): PartGridGroupModel {
        return {...d} as PartGridGroupModel;
    }
}

export default CategorizationReviewPageTableController